// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A timeline event component for displaying asset activities.
 * This module provides a consistent visual representation for
 * different types of events in an asset's history, including
 * location changes, actions, activities, and ownership changes.
 * Each event type has its own icon and styling.
 *
 * Key features:
 * - Type-specific icons
 * - Hover interactions
 * - Tooltips
 * - Edit capabilities
 * - Visual connectors
 */
import {
  CorporateFareRounded as CorporateFareIcon,
  EditRounded,
  LocalShippingRounded as LocalShippingIcon,
  SellOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import { PropsWithChildren, ReactNode } from 'react';

/**
 * Styled container for timeline events.
 * Uses CSS Grid for layout with subgrid support
 * to align with parent timeline grid.
 */
const EventContainer = styled(Box)({
  display: 'grid',
  alignItems: 'center',
  gridColumn: 'span 2',
  gridTemplateColumns: 'subgrid',

  '&:last-child': {
    '.connector': {
      backgroundColor: 'transparent',
    },
  },

  '.edit-button': {
    opacity: 0,
  },

  '&:hover': {
    '.edit-button': {
      opacity: 1,
    },
  },
});

/**
 * Styled vertical line connecting timeline events.
 * Creates a visual connection between consecutive events.
 */
const EventConnector = styled(Box)(({ theme }) => ({
  alignSelf: 'stretch',
  width: '2px',
  minHeight: '1rem',
  justifySelf: 'center',
  borderRadius: 'calc(infinity * 1px)',
  backgroundColor: theme.palette.primary.main,
}));

/**
 * Styled dot indicator for action events.
 * Provides a small circular marker in the timeline.
 */
const EventIndicator = styled(Box)(({ theme }) => ({
  width: 6,
  height: 6,
  backgroundColor: theme.palette.primary.main,
  justifySelf: 'center',
  borderRadius: 'calc(infinity * 1px)',
}));

/**
 * Props for configuring the AssetTimelineEvent component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The type of event to display.
   * Determines which icon is shown.
   *
   * - location: Building icon for location changes
   * - action: Dot indicator for general actions
   * - activity: Truck icon for shipping/movement
   * - ownership: Tag icon for ownership changes
   */
  type: 'location' | 'action' | 'activity' | 'ownership';

  /**
   * Primary text displayed next to the event.
   * Should be short and descriptive.
   *
   * @example "Moved to Warehouse A"
   */
  label: string;

  /**
   * Optional tooltip content shown on hover.
   * Can include additional details or context.
   */
  tooltip?: ReactNode;

  /**
   * Optional secondary text displayed after the label.
   * Used for additional context or details.
   *
   * @example "Via UPS Ground"
   */
  description?: string;

  /**
   * Optional callback fired when edit button is clicked.
   * When provided, shows an edit button on hover.
   */
  onEdit?: () => void;
}

/**
 * A timeline event component for asset activities.
 * Displays a single event in an asset's timeline with
 * appropriate icon, label, and optional description.
 *
 * Features:
 * - Type-specific icons (building, truck, tag)
 * - Hover-to-edit interaction
 * - Tooltip support
 * - Visual connectors between events
 * - Support for child content
 *
 * Usage example:
 * ```tsx
 * <AssetTimelineEvent
 *   type="location"
 *   label="Moved to Warehouse A"
 *   description="Via UPS Ground"
 *   tooltip="Delivery completed on time"
 *   onEdit={() => handleEdit()}
 * >
 *   <EventDetails />
 * </AssetTimelineEvent>
 * ```
 */
export function AssetTimelineEvent(props: PropsWithChildren<Props>) {
  let indicator: ReactNode;

  if (props.type === 'action') {
    indicator = <EventIndicator />;
  }

  if (props.type === 'location') {
    indicator = <CorporateFareIcon sx={{ marginTop: 1 }} />;
  }

  if (props.type === 'activity') {
    indicator = <LocalShippingIcon sx={{ marginTop: 1 }} />;
  }

  if (props.type === 'ownership') {
    indicator = <SellOutlined sx={{ marginTop: 1 }} />;
  }

  const marginTop = props.type === 'action' ? 0 : 1;
  const marginBottom = props.children === undefined ? 0 : 2;
  const isEditable = props.onEdit !== undefined;

  return (
    <Tooltip arrow placement="top" title={props.tooltip}>
      <EventContainer>
        {indicator}
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          marginTop={marginTop}
        >
          <Typography variant="subtitle2">{props.label}</Typography>
          {props.description && (
            <Typography variant="body2" color="text.secondary">
              {props.description}
            </Typography>
          )}
          {isEditable && (
            <IconButton
              size="small"
              className="edit-button"
              onClick={props.onEdit}
            >
              <EditRounded fontSize="small" />
            </IconButton>
          )}
        </Stack>
        <EventConnector marginTop={marginTop} className="connector" />
        <Box gridColumn="2 / 3" marginBottom={marginBottom}>
          {props.children}
        </Box>
      </EventContainer>
    </Tooltip>
  );
}
